import type { ComponentProps } from "react";
import type { Badge } from "@knowtex-ai/components";
import {
  isActiveRun,
  type McArtifact,
  MC_CUSTOM_PLAYBOOK,
  type McPlaybook,
  type McIssueStatus,
  type McRun,
} from "../../lib/mc";

type BadgeVariant = NonNullable<ComponentProps<typeof Badge>["variant"]>;

export interface BadgeSpec {
  label: string;
  variant: BadgeVariant;
  tip?: string;
}

const STATUS_BADGES: Record<string, BadgeSpec> = {
  requested: { label: "Requested", variant: "default" },
  provisioning: { label: "Provisioning", variant: "info" },
  running: { label: "Running", variant: "info" },
  finalizing: { label: "Finalizing", variant: "info" },
  completed: { label: "Completed", variant: "success" },
  failed: { label: "Failed", variant: "error" },
  cancelled: { label: "Cancelled", variant: "default" },
};

const OUTCOME_LABELS: Record<string, string> = {
  passed: "Passed",
  issues_found: "Issues found",
  inconclusive: "Inconclusive",
  not_tested: "Not tested",
};

const SEVERITY_VARIANTS: Record<string, BadgeVariant> = {
  critical: "error",
  high: "error",
  medium: "warning",
  low: "info",
};

export const statusBadge = (status?: string): BadgeSpec =>
  (status && STATUS_BADGES[status]) || {
    label: status || "Unknown",
    variant: "default",
  };

export const outcomeLabel = (outcome?: string): string | null =>
  outcome ? OUTCOME_LABELS[outcome] || outcome : null;

export interface ResultSpec {
  label: string;
  /** Why it errored, shown behind an info icon */
  tip?: string;
}

export const severityVariant = (severity?: string): BadgeVariant =>
  (severity && SEVERITY_VARIANTS[severity]) || "default";

// MC notes a plain cancel as "cancelled by X before the worker started"; it appends "; …" when something went wrong,
// e.g. "cancelled by X; the worker stopped before reporting back".
const PLAIN_CANCEL_NOTE = /^cancelled\b[^;]*$/i;

/** What went wrong in a run, or null when nothing did (including a plain cancel). */
export const runProblem = (
  run: Pick<McRun, "status" | "error">,
): string | null => {
  if (run.status === "failed") return run.error || "The run failed.";
  if (
    run.status === "cancelled" &&
    run.error &&
    !PLAIN_CANCEL_NOTE.test(run.error.trim())
  )
    return run.error;
  return null;
};

/** The run's result: an error when it failed or was cut short by a problem, else its outcome (with the finding count when it found issues). */
export const runResult = (
  run: Pick<McRun, "status" | "error" | "outcome" | "findings">,
): ResultSpec | null => {
  const problem = runProblem(run);
  if (problem) return { label: "Error", tip: problem };
  const label = outcomeLabel(run.outcome);
  if (!label) return null;
  return run.outcome === "issues_found"
    ? { label: `${label} (${formatCount(run.findings?.length ?? 0)})` }
    : { label };
};

export const formatElapsed = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

export const spanMs = (
  startIso: string | undefined,
  endIso: string | undefined,
  now: number | null,
): number => {
  const start = Date.parse(startIso || "");
  const end = endIso ? Date.parse(endIso) : (now ?? NaN);
  return end - start;
};

export const durationMs = (run: McRun, now = Date.now()): number =>
  spanMs(
    run.startedAt || run.createdAt,
    run.finishedAt,
    isActiveRun(run) ? now : null,
  );

export const formatDuration = (run: McRun, now = Date.now()): string =>
  formatElapsed(durationMs(run, now));

const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});
const COUNT = new Intl.NumberFormat("en-US");
const ONE_DECIMAL = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const formatUsd = (usd: number): string => USD.format(Number(usd || 0));

/** Active runs report cost as it accrues, so a zero only means "nothing yet" until the run ends. */
export const formatCost = (run: McRun): string =>
  !isActiveRun(run) || run.costUsd > 0 ? formatUsd(run.costUsd) : "—";

export const formatCount = (value: number): string =>
  COUNT.format(Number(value || 0));

const PLURAL = new Intl.PluralRules("en-US");

/** e.g. "1 time", "1,028 times" */
export const countOf = (value: number, one: string, other = `${one}s`) =>
  `${formatCount(value)} ${PLURAL.select(Number(value || 0)) === "one" ? one : other}`;

/** The PR a run is about: the one it was asked to test, or the one its deploy shipped. */
export const runPr = (run: McRun): number | undefined =>
  run.input?.pr ?? run.source?.pr;

export const githubPrUrl = (repo: string, pr: number) =>
  `https://github.com/${repo}/pull/${pr}`;

export const formatTrigger = (run: McRun): string => {
  const parts: string[] = [run.trigger || "unknown"];
  if (run.sha) parts.push(run.sha.slice(0, 7));
  const pr = runPr(run);
  if (pr) parts.push(`PR #${pr}`);
  return parts.join(" ");
};

export const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

/** One line for a run's header, e.g. "Manual run by ada@knowtex.ai started Oct 18, …". */
export const formatRunContext = (
  run: McRun,
  options: TimeOptions = {},
): string => {
  let text = `${capitalize(run.trigger || "unknown")} run`;
  if (run.sha) text += ` at ${run.sha.slice(0, 7)}`;
  const pr = runPr(run);
  if (pr) text += ` for PR #${pr}`;
  if (run.requestedBy) text += ` by ${run.requestedBy}`;
  return run.startedAt
    ? `${text} started ${formatDateTime(run.startedAt, options)}`
    : `${text} requested ${formatDateTime(run.createdAt, options)}`;
};

export const ISSUE_STATUS_BADGES: Record<McIssueStatus, BadgeSpec> = {
  new: {
    label: "New",
    variant: "info",
    tip: "Not triaged yet; runs keep reporting it",
  },
  confirmed: { label: "Confirmed", variant: "warning" },
  regressed: {
    label: "Regressed",
    variant: "error",
    tip: "Seen again after it was marked fixed",
  },
  fixed: { label: "Fixed", variant: "success" },
  false_positive: {
    label: "False positive",
    variant: "default",
    tip: "Runs stop reporting it",
  },
  wont_fix: {
    label: "Won't fix",
    variant: "default",
    tip: "Runs stop reporting it",
  },
};

/** Statuses an engineer can pick; regressed is set when a fixed issue shows up again. */
export const ISSUE_STATUS_ORDER: McIssueStatus[] = [
  "new",
  "confirmed",
  "regressed",
  "fixed",
  "false_positive",
  "wont_fix",
];

export const issueRef = (issueId: number): string => `I-${issueId}`;

export interface RunPlaybook {
  title: string;
  /** Undefined for custom runs, and for playbooks the target no longer has */
  playbook?: McPlaybook;
  custom: boolean;
  /** The run's instructions (for older custom runs, what they were asked to do) */
  instructions?: string;
}

const LEGACY_DEFAULT_GOAL = "Run the playbook.";

/**
 * Which playbook a run used. Older runs recorded a goal and maybe a preset instead: a preset maps to
 * the playbook with the same id, the plain default goal to the default playbook, anything else is custom.
 */
export const runPlaybook = (
  input: McRun["input"] | undefined,
  playbooks: McPlaybook[],
): RunPlaybook => {
  const instructions = input?.guidanceText?.trim() || undefined;
  if (input?.playbook === MC_CUSTOM_PLAYBOOK) {
    return { title: "Custom", custom: true, instructions };
  }
  const id = input?.playbook ?? input?.preset;
  if (id) {
    const playbook = playbooks.find((p) => p.id === id);
    return {
      title: playbook?.title ?? input?.playbookTitle ?? id,
      playbook,
      custom: false,
      instructions,
    };
  }
  const goal = input?.goal?.trim();
  if (!goal || goal === LEGACY_DEFAULT_GOAL) {
    const playbook = playbooks.find((p) => p.default) ?? playbooks[0];
    if (playbook)
      return { title: playbook.title, playbook, custom: false, instructions };
  }
  return {
    title: "Custom",
    custom: true,
    instructions:
      [goal === LEGACY_DEFAULT_GOAL ? undefined : goal, instructions]
        .filter(Boolean)
        .join("\n\n") || undefined,
  };
};

/** Where a playbook is shown on the playbooks page. */
export const playbookAnchor = (playbookId: string): string =>
  `playbook-${playbookId}`;

/** The playbooks page at a playbook, or at one version in its history. */
export const playbookUrl = (playbookId: string, version?: number): string =>
  version
    ? `/mc/playbooks?${new URLSearchParams({ history: playbookId, version: String(version) })}`
    : `/mc/playbooks#${playbookAnchor(playbookId)}`;

export const firstLine = (text: string | undefined, max = 140): string => {
  const line = String(text || "")
    .split("\n")[0]
    .trim();
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
};

export interface TimeOptions {
  /** Defaults to the viewer's zone */
  timeZone?: string;
}

const DAY: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

const formatIn = (
  date: Date,
  timeZone: string | undefined,
  options: Intl.DateTimeFormatOptions,
) => new Intl.DateTimeFormat("en-US", { ...options, timeZone }).format(date);

const isUtc = (timeZone: string | undefined) =>
  ["UTC", "Etc/UTC", "Etc/GMT", "GMT"].includes(
    new Intl.DateTimeFormat("en-US", { timeZone }).resolvedOptions().timeZone,
  );

export interface TimestampParts {
  local: string;
  /** Omitted when the viewer is already on UTC */
  utc?: string;
}

const timestampParts = (
  iso: string | undefined,
  {
    timeZone,
    date: withDate,
    seconds,
  }: TimeOptions & { date: boolean; seconds: boolean },
): TimestampParts => {
  if (!iso) return { local: "—" };
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return { local: iso };
  const second: Intl.DateTimeFormatOptions = seconds
    ? { second: "2-digit" }
    : {};
  let local = formatIn(date, timeZone, {
    hour: "numeric",
    minute: "2-digit",
    ...second,
    timeZoneName: "short",
  });
  if (withDate) local = `${formatIn(date, timeZone, DAY)} at ${local}`;
  if (isUtc(timeZone)) return { local };
  let utc = formatIn(date, "UTC", {
    hour: "2-digit",
    minute: "2-digit",
    ...second,
    hourCycle: "h23",
  });
  if (
    withDate &&
    formatIn(date, timeZone, DAY) !== formatIn(date, "UTC", DAY)
  ) {
    utc = `${formatIn(date, "UTC", {
      month: "short",
      day: "numeric",
    })} at ${utc}`;
  }
  return { local, utc: `${utc} UTC` };
};

const joinParts = ({ local, utc }: TimestampParts) =>
  utc ? `${local} (${utc})` : local;

/** Parts of `formatDateTime`, for layouts that put UTC on its own line. */
export const dateTimeParts = (
  iso?: string,
  options: TimeOptions = {},
): TimestampParts =>
  timestampParts(iso, { ...options, date: true, seconds: false });

/** Parts of `formatTime`, for layouts that put UTC on its own line. */
export const timeParts = (
  iso?: string,
  options: TimeOptions = {},
): TimestampParts =>
  timestampParts(iso, { ...options, date: false, seconds: true });

/** e.g. "Oct 18, 2026 at 3:41 PM EDT (19:41 UTC)": the viewer's zone, then UTC. */
export const formatDateTime = (
  iso?: string,
  options: TimeOptions = {},
): string => joinParts(dateTimeParts(iso, options));

/** e.g. "3:41:05 PM EDT (19:41:05 UTC)", for timelines within one run. */
export const formatTime = (iso?: string, options: TimeOptions = {}): string =>
  joinParts(timeParts(iso, options));

export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${formatCount(bytes)} B`;
  if (bytes < 1024 * 1024) return `${ONE_DECIMAL.format(bytes / 1024)} KB`;
  if (bytes < 1024 ** 3) return `${ONE_DECIMAL.format(bytes / 1024 ** 2)} MB`;
  return `${ONE_DECIMAL.format(bytes / 1024 ** 3)} GB`;
};

export type ArtifactKind = "image" | "video" | "audio" | "file";

export const artifactKind = (mime = ""): ArtifactKind => {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return "file";
};

export const groupArtifacts = (
  artifacts: McArtifact[] = [],
): Record<ArtifactKind, McArtifact[]> =>
  artifacts.reduce<Record<ArtifactKind, McArtifact[]>>(
    (groups, artifact) => {
      groups[artifactKind(artifact.mime)].push(artifact);
      return groups;
    },
    { image: [], video: [], audio: [], file: [] },
  );
